import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Package } from "lucide-react";
import { apiClient } from "../../services/apiClientService";

function formatAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const NotificationSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await apiClient("GET", "/api/orders/notifications", null, {
          skipLoading: true,
        });
        if (!cancelled) {
          setItems(Array.isArray(res?.notifications) ? res.notifications : []);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.message || "Could not load notifications.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-2xl border border-stone-200/90 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700 ring-1 ring-orange-200/80">
          <Bell className="h-5 w-5" strokeWidth={2.25} aria-hidden />
        </span>
        <div>
          <h2 className="text-lg font-bold text-stone-900">Notifications</h2>
          <p className="text-xs text-stone-500">Order updates from sellers.</p>
        </div>
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-stone-500">Loading…</p>
      ) : err ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/60 px-6 py-14 text-center">
          <Package className="mx-auto h-10 w-10 text-stone-300" strokeWidth={1.5} aria-hidden />
          <p className="mt-3 text-sm font-medium text-stone-600">No notifications yet</p>
          <p className="mt-1 text-xs text-stone-500">
            When a seller marks your order ready, it will appear here.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 rounded-xl border border-stone-100 bg-stone-50/40">
          {items.map((n) => (
            <li key={n.id} className="p-4 transition hover:bg-white/90">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-stone-900">{n.title}</p>
                  <p className="mt-1 text-sm text-stone-600">{n.message}</p>
                  <p className="mt-2 text-xs text-stone-400">{formatAt(n.at)}</p>
                </div>
                <Link
                  to={`/order/${n.orderId}`}
                  className="mt-2 shrink-0 text-sm font-bold text-orange-700 hover:text-orange-900 sm:mt-0"
                >
                  View order →
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NotificationSection;
